import { logWarning } from '@databases/logs';
import { type RefObject, useEffect, useCallback } from 'react';

/**
 * Hook that detects clicks outside of the passed ref
 */
const useHandleOutsideClick = <T extends HTMLElement>(
  onOutsideClick: () => void,
  ref?: RefObject<T>,
  // list of css selectors that should not trigger the outside click
  otherTargets: string[] = [],
  // whether we should be listening to click events.
  shouldListen = true
) => {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element;

      const clickedInsideRef = ref?.current && ref.current.contains(target);

      const clickedInsideOtherTargets = otherTargets.some((otherTarget) => {
        try {
          return target.closest(otherTarget) !== null;
        } catch (error) {
          if (error instanceof Error) {
            logWarning(error);
          }
          return false;
        }
      });

      if (!clickedInsideRef && !clickedInsideOtherTargets) {
        onOutsideClick();
      }
    },
    [onOutsideClick, otherTargets, ref]
  );

  // TODO: CTD-4070: Investigate why mousedown event is not always triggered.
  useEffect(() => {
    if (!shouldListen) {
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // eslint-disable-next-line no-restricted-globals
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onOutsideClick, handleClickOutside, shouldListen]);
};

export default useHandleOutsideClick;
